import React, { useEffect, useRef, useState } from 'react';
export type Frame = {
  label: string;
  title: string;
  src: string;
  alt: string;
  caption?: string;
};

/**
 * Steps through a sequence of stills, one at a time.
 *
 * The stages it shows were captured off a live three.js scene, but shipping that
 * scene into this page would mean loading tens of megabytes of GLB per character
 * to animate something the reader looks at for a few seconds. Stills carry the
 * same argument at a hundredth of the weight.
 *
 * Frames are stacked and cross-faded rather than swapped, so the figure never
 * collapses to zero height between two images of different proportions.
 */
export function FrameViewer({
  frames,
  hold = 3200
}: {
  frames: Frame[];
  hold?: number;
}) {
  const [at, setAt] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  const timer = useRef<number | null>(null);
  useEffect(() => {
    if (!playing || failed) return;
    timer.current = window.setTimeout(() => {
      setAt(current => (current + 1) % frames.length);
    }, hold);
    return () => {
      if (timer.current) window.clearTimeout(timer.current);
    };
  }, [at, playing, hold, frames.length, failed]);
  const frame = frames[at];
  if (failed) {
    return <div className="flex aspect-video flex-col items-start justify-end border border-dashed border-rule bg-surface/60 p-6" data-id="element-20" data-mp="1e514dcvynwsi">
        <p className="text-[15px] font-medium text-ink-muted" data-id="element-21" data-mp="qckl4z0hlyrm">Pipeline walk-through</p>
        <p className="mt-2 text-[13px] leading-relaxed text-ink-faint" data-id="element-22" data-mp="lhnbpqzapne7">
          Images did not load. Check the URLs in <code data-id="element-23" data-mp="106jkjq96f4fv">data/assets.ts</code>.
        </p>
      </div>;
  }
  return <div data-id="element-24" data-mp="vcp2ssd4szjf">
      <div className="relative overflow-hidden bg-surface" data-id="element-25" data-mp="vbx8dkv9t7uq">
        <div className="relative aspect-video" data-id="element-26" data-mp="7ne0v7k1oz3p">
          {frames.map((item, index) => <img key={item.src} src={item.src} alt={item.alt} onLoad={() => setLoaded(true)} onError={() => setFailed(true)} className={`absolute inset-0 h-full w-full object-contain transition-opacity duration-500 ${index === at ? 'opacity-100' : 'opacity-0'}`} data-id="element-27" data-mp="2q49hmd3idd8" data-mpi={index} />)}

          {!loaded && <div className="absolute inset-0 grid place-items-center text-[13px] text-ink-faint" data-id="element-28" data-mp="1643g78u7oq3w">
              loading…
            </div>}
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-1.5" data-id="element-29" data-mp="dh91ytnzmmyv">
        {frames.map((item, index) => <button key={item.label} type="button" aria-current={index === at} onClick={() => {
        setPlaying(false);
        setAt(index);
      }} className={`rounded-md border px-2.5 py-1 text-[12px] transition-colors duration-150 ease-expo ${index === at ? 'border-ink/20 text-ink' : 'border-rule text-ink-faint hover:text-ink-muted'}`} data-id="element-30" data-mp="ieiz6vhmc2x9" data-mpi={index}>
            <span className="tabular-nums opacity-60" data-id="element-31" data-mp="3edt5z6einjmn">{item.label}</span> {item.title}
          </button>)}
        <button type="button" onClick={() => setPlaying(current => !current)} className="ml-auto rounded-md border border-rule px-2.5 py-1 text-[12px] text-ink-faint transition-colors duration-150 ease-expo hover:text-ink" data-id="element-32" data-mp="39k74899w505m">
          {playing ? 'Pause' : 'Play'}
        </button>
      </div>

      {frame.caption && <p className="mt-3 text-[13px] leading-relaxed text-ink-faint" data-id="element-33" data-mp="2qmdzr2jmci64">{frame.caption}</p>}
    </div>;
}